import * as React from 'react';
import { Subject } from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {IApiQueryData} from 'PyzShopUi/scripts/product-review/interfaces';
import usePrevious from 'PyzShopUi/scripts/shop-ui/hooks/usePrevious';
import {getProductReviewData} from 'PyzShopUi/scripts/product-review/actions/getReview';
import isEqual from 'react-fast-compare';
import { LIST_LOAD_DEBOUNCE_TIME } from 'PyzShopUi/scripts/product-review/constants';

interface IUpdateObject {
    dispatch: React.Dispatch<any>;
    apiQueryData: IApiQueryData;
}

function useApiQueryDataUpdate(apiQueryData: IApiQueryData, dispatch: React.Dispatch<any>) {
    const updateProductReviewDataObserver = React.useRef(new Subject<IUpdateObject>());
    const previousApiQueryData = usePrevious(apiQueryData);

    React.useEffect(() => {
        const subscription = updateProductReviewDataObserver.current
            .pipe(debounceTime(LIST_LOAD_DEBOUNCE_TIME))
            .subscribe(object => {
                object.dispatch(
                    getProductReviewData(
                        object.apiQueryData
                    )
                );
            });

        return () => {
            subscription.unsubscribe();
        }
    }, []);

    React.useEffect(() => {
        if (!isAllowToUpdate(apiQueryData, previousApiQueryData)) {
            return;
        }

        updateProductReviewDataObserver.current.next({
            dispatch,
            apiQueryData
        });
    }, [apiQueryData]);
}

const isAllowToUpdate = (apiQueryData: IApiQueryData, previousApiQueryData: IApiQueryData): boolean => (
    previousApiQueryData !== undefined &&
    Object.keys(apiQueryData).length &&
    !isEqual(copyAndIgnoringEmptyKeys(previousApiQueryData), copyAndIgnoringEmptyKeys(apiQueryData)) &&
    +apiQueryData.page !== 0 //Prevent api call when empty list is returned
)

function copyAndIgnoringEmptyKeys(input: object): object {
    const output = {};

    if (!input) {
        return output;
    }

    Object.keys(input).forEach(key => {
        if (input[key] === undefined
            || input[key] === null
            || (typeof input[key] === 'object' && Object.keys(input[key]).length === 0)
        ) {
            return;
        }

        output[key] = input[key];
    });

    return output;
}

export default useApiQueryDataUpdate;
